import { action, computed, makeObservable, observable } from "mobx";
import { RootStore } from "src/stores/RootStore";
import {
    INVOICE_FILE_STATE,
    INVOICE_LIST_STATE,
    INVOICE_STATE,
    INVOICE_TYPE,
    SORT,
    WORKFLOWUSER_ROLE
} from "./InvoiceEnums";
import {
    IAssignedUser,
    ICreditor,
    IInvoiceFile,
    IInvoiceFilter,
    IInvoiceProperties,
    IRealestate,
    ISearchTag
} from "./InvoiceTypes";
import { GetRealestateUserAndAccessData_realestateUsersAndAccess } from "src/api/generated/GetRealestateUserAndAccessData";
import { format } from "date-fns";
import i18next from "i18next";
import { persist } from "mobx-persist";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey } from "src/network/User";
import { MessageType } from "src/components/notifications/Notifier";
import { Route, selectRoute } from "src/config/routes";
import Axios from "src/network/Axios";
import {
    GetAllInvoices,
    GetAllInvoices_cred_invoices,
    GetAllInvoices_cred_invoices_insertuserinfo,
    GetAllInvoices_cred_invoices_workflowusers
} from "src/api/generated/GetAllInvoices";
import { GET_ALL_INVOICES, GET_ALL_OPEN_INVOICES } from "src/api/cred";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { Invoice } from "./InvoiceStore";
import {
    GetInvoiceByNumber_cred_invoices_insertuserinfo,
    GetInvoiceByNumber_cred_invoices_workflowusers
} from "src/api/generated/GetInvoiceByNumber";

type BatchProcessMap = {
    [invoiceNumber: number]: boolean;
};

type ListStateSelectedMap = {
    [K in INVOICE_LIST_STATE]: boolean;
};

const INVOICE_LIST_REFRESH_TIME_IN_SECONDS = 300;

export const getInvoiceStateById = (stateId: number): INVOICE_STATE => {
    switch (stateId) {
        case 0:
            return INVOICE_STATE.UNDEFINED;
        case 10:
            return INVOICE_STATE.NEW;
        case 20:
            return INVOICE_STATE.ASSIGN_WORKFLOW_USERS;
        case 25:
            return INVOICE_STATE.RESTART_AFTER_REJECTED;
        case 30:
            return INVOICE_STATE.READY_FOR_VISA;
        case 41:
            return INVOICE_STATE.VISA_1_OK;
        case 42:
            return INVOICE_STATE.VISA_2_OK;
        case 43:
            return INVOICE_STATE.VISA_3_OK;
        case 49:
            return INVOICE_STATE.FULLY_SIGHTED;
        case 50:
            return INVOICE_STATE.SENT_TO_ERP;
        case 60:
            return INVOICE_STATE.IMPORTED_IN_ERP;
        case 90:
            return INVOICE_STATE.PAID;
        case 95:
            return INVOICE_STATE.REJECTED;
        case 99:
            return INVOICE_STATE.CANCELLED;
        default:
            return INVOICE_STATE.NEW;
    }
};

export class InvoiceListStore {
    rootStore: RootStore;

    invoices: Invoice[] = [];

    invoiceFilter: IInvoiceFilter = {
        state: INVOICE_LIST_STATE.NEW,
        sort: SORT.DATE_NEWEST,
        query: "",
        types: [],
        showOnlyMyInvoices: true,
        showOnlyInvoicesFromMyRealestates: false
    };

    searchTags: ISearchTag[] = [
        {
            label: i18next.t("screens.kredi_flow.list.tags.only_mine"),
            id: "SHOW_ONLY_MY_OWN_INVOICES",
            active: this.invoiceFilter.showOnlyMyInvoices
        },
        {
            label: i18next.t("screens.kredi_flow.list.tags.my_realestates"),
            id: "SHOW_ONLY_INVOICES_FROM_MY_REALESTATES",
            active: false
        },
        { label: i18next.t("screens.kredi_flow.invoice_type.1"), id: "2", active: false, type: INVOICE_TYPE.INVOICE },
        { label: i18next.t("screens.kredi_flow.invoice_type.2"), id: "3", active: false, type: INVOICE_TYPE.CREDIT }
    ];

    realestateUserData: GetRealestateUserAndAccessData_realestateUsersAndAccess[] = [];

    loadAllInvoices: boolean = false;

    @persist("object")
    batchProcessMap: BatchProcessMap = {};

    @persist
    hasBatchProcessMapInitialized: boolean = false;

    @persist("object")
    areAllInvoicesOfStateSelected: ListStateSelectedMap = {
        [INVOICE_LIST_STATE.NEW]: false,
        [INVOICE_LIST_STATE.TO_APPROVE]: false,
        [INVOICE_LIST_STATE.PAY]: false,
        [INVOICE_LIST_STATE.PAID]: false,
        [INVOICE_LIST_STATE.REJECTED]: false,
        [INVOICE_LIST_STATE.CANCELLED]: false,
        [INVOICE_LIST_STATE.ALL]: false,
        [INVOICE_LIST_STATE.ALL_OPEN]: false,
        [INVOICE_LIST_STATE.ADDITIONAL_INVOICES]: false,
        [INVOICE_LIST_STATE.IMPORT_DOCUMENTS]: false
    };

    @persist
    isBatchProcessing: boolean = false;

    @persist
    currentBatchProcessIndex: number = 0;

    isDownloadingInvoiceFiles: boolean = false;

    initialLoadInvoicesCompleted: boolean = false;

    isLoadingInvoices: boolean = false;

    loadInvoicesTimer?: NodeJS.Timer;
    lastReloadTime?: Date;
    currentTime: Date = new Date();

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            invoiceFilter: observable,
            realestateUserData: observable,
            searchTags: observable,
            loadAllInvoices: observable,
            batchProcessMap: observable,
            areAllInvoicesOfStateSelected: observable,
            isBatchProcessing: observable,
            currentBatchProcessIndex: observable,
            hasBatchProcessMapInitialized: observable,
            isDownloadingInvoiceFiles: observable,
            lastReloadTime: observable,
            currentTime: observable,
            loadInvoicesTimer: observable,
            initialLoadInvoicesCompleted: observable,
            invoices: observable,
            isLoadingInvoices: observable,

            setFilter: action,
            setFilterState: action,
            clearFilter: action,
            setTag: action,
            setRealestateUserData: action,
            setLoadAllInvoices: action,
            setBatchProcessMapEntry: action,
            toggleSelectAllInvoicesOfState: action,
            setIsBatchProcessing: action,
            setNextBatchProcessIndex: action,
            setPreviousBatchProcessIndex: action,
            setBatchProcessMapInitialized: action,
            terminateBatchProcess: action,
            setCurrentBatchProcessIndex: action,
            downloadInvoiceFilesZip: action,
            navigateToNextBatchProcess: action,
            setIsDownloadingInvoiceFiles: action,
            handleBatchProcessCheckboxClick: action,
            deselectAllInvoices: action,
            resetAreAllInvoicesOfStateSelected: action,
            setLastReloadTime: action,
            updateCurrentTime: action,
            loadInvoices: action,
            initialLoadInvoices: action,
            setInvoices: action,
            setInitialLoadInvoicesCompleted: action,
            clearLoadInvoicesTimer: action,
            startLoadInvoicesTimer: action,
            setIsLoadingInvoices: action,

            invoiceStats: computed,
            filteredInvoices: computed,
            selectedBatchProcessInvoiceNumbers: computed,
            hasAnySelectedBatchProcessInvoice: computed,
            nextBatchProcessInvoiceNumber: computed,
            previousBatchProcessInvoiceNumber: computed,
            selectedFileIds: computed,
            lastReloadTimeText: computed
        });

        setInterval(() => {
            this.updateCurrentTime();
        }, 30000); // Update every 30 seconds
    }

    setIsDownloadingInvoiceFiles = (isDownloadingInvoiceFiles: boolean) => {
        this.isDownloadingInvoiceFiles = isDownloadingInvoiceFiles;
    };

    setIsLoadingInvoices = (isLoadingInvoices: boolean) => {
        this.isLoadingInvoices = isLoadingInvoices;
    };

    setBatchProcessMapEntry = (invoiceNumber: number, batchProcess: boolean) => {
        this.batchProcessMap[invoiceNumber] = batchProcess;
    };

    getBatchProcessMapEntry = (invoiceNumber: number) => {
        return this.batchProcessMap[invoiceNumber] ?? false;
    };

    convertInvoiceStateToInvoiceListState = (state: INVOICE_STATE): INVOICE_LIST_STATE => {
        const invoiceStateNumber = Number(state);
        let convertedInvoiceListFilterState = INVOICE_LIST_STATE.ALL;

        switch (true) {
            case invoiceStateNumber >= INVOICE_STATE.NEW && invoiceStateNumber <= INVOICE_STATE.RESTART_AFTER_REJECTED:
                convertedInvoiceListFilterState = INVOICE_LIST_STATE.NEW;
                break;
            case invoiceStateNumber >= INVOICE_STATE.READY_FOR_VISA &&
                invoiceStateNumber <= INVOICE_STATE.FULLY_SIGHTED:
                convertedInvoiceListFilterState = INVOICE_LIST_STATE.TO_APPROVE;
                break;
            case invoiceStateNumber >= INVOICE_STATE.SENT_TO_ERP && invoiceStateNumber <= INVOICE_STATE.IMPORTED_IN_ERP:
                convertedInvoiceListFilterState = INVOICE_LIST_STATE.PAY;
                break;
            case invoiceStateNumber === INVOICE_STATE.PAID:
                convertedInvoiceListFilterState = INVOICE_LIST_STATE.PAID;
                break;
            case invoiceStateNumber === INVOICE_STATE.REJECTED:
                convertedInvoiceListFilterState = INVOICE_LIST_STATE.REJECTED;
                break;
            case invoiceStateNumber === INVOICE_STATE.CANCELLED:
                convertedInvoiceListFilterState = INVOICE_LIST_STATE.CANCELLED;
                break;
        }

        return convertedInvoiceListFilterState;
    };

    clearFilter = () => {
        this.searchTags = [...this.searchTags].map((t) => {
            return { ...t, active: false };
        });

        this.setFilter({
            state: this.invoiceFilter.state, // when clearing the filter, keep the state (stay in the same tab)
            sort: SORT.DATE_NEWEST,
            query: "",
            types: [],
            showOnlyMyInvoices: false,
            showOnlyInvoicesFromMyRealestates: false
        });
    };

    setTag = (id: string, active: boolean) => {
        const newSearchTags: ISearchTag[] = [...this.searchTags].map((searchTag) => {
            if (searchTag.id === id) {
                return { ...searchTag, active: active };
            }
            return searchTag;
        });

        const activeSearchTags: INVOICE_TYPE[] = [];

        newSearchTags.forEach((tag) => {
            if (tag.active && tag.type) {
                activeSearchTags.push(tag.type);
            }
        });

        this.searchTags = newSearchTags;

        this.setFilter({
            ...this.invoiceFilter,
            types: activeSearchTags,
            showOnlyMyInvoices:
                newSearchTags.find((searchTag) => searchTag.id === "SHOW_ONLY_MY_OWN_INVOICES")?.active ?? false,
            showOnlyInvoicesFromMyRealestates:
                newSearchTags.find((st) => st.id === "SHOW_ONLY_INVOICES_FROM_MY_REALESTATES")?.active ?? false
        });
    };

    // Setters
    setFilter = (newFilter: IInvoiceFilter) => {
        this.invoiceFilter = newFilter;
    };

    setFilterState = (state: INVOICE_LIST_STATE) => {
        this.setFilter({ ...this.invoiceFilter, state });
    };

    setRealestateUserData = (realestateUserData: GetRealestateUserAndAccessData_realestateUsersAndAccess[]) => {
        this.realestateUserData = realestateUserData;
    };

    setLoadAllInvoices = (loadAllInvoices: boolean) => {
        this.loadAllInvoices = loadAllInvoices;
    };

    toggleSelectAllInvoicesOfState = (invoiceListState: INVOICE_LIST_STATE) => {
        const selected = this.areAllInvoicesOfStateSelected[invoiceListState] ?? false;
        const newSelected = !selected;

        if (invoiceListState === INVOICE_LIST_STATE.ALL) {
            this.areAllInvoicesOfStateSelected = {
                ...this.areAllInvoicesOfStateSelected,
                [INVOICE_LIST_STATE.NEW]: newSelected,
                [INVOICE_LIST_STATE.TO_APPROVE]: newSelected,
                [INVOICE_LIST_STATE.PAY]: newSelected,
                [INVOICE_LIST_STATE.PAID]: newSelected,
                [INVOICE_LIST_STATE.REJECTED]: newSelected,
                [INVOICE_LIST_STATE.CANCELLED]: newSelected,
                [INVOICE_LIST_STATE.ALL_OPEN]: newSelected,
                [INVOICE_LIST_STATE.ALL]: newSelected
            };
        } else if (invoiceListState === INVOICE_LIST_STATE.ALL_OPEN) {
            this.areAllInvoicesOfStateSelected = {
                ...this.areAllInvoicesOfStateSelected,
                [INVOICE_LIST_STATE.NEW]: newSelected,
                [INVOICE_LIST_STATE.TO_APPROVE]: newSelected,
                [INVOICE_LIST_STATE.PAY]: newSelected,
                [INVOICE_LIST_STATE.REJECTED]: newSelected,
                [INVOICE_LIST_STATE.ALL_OPEN]: newSelected
            };
        } else {
            this.areAllInvoicesOfStateSelected = {
                ...this.areAllInvoicesOfStateSelected,
                [invoiceListState]: newSelected
            };
        }

        const invoices = this.filteredInvoices.filter((invoice) => {
            if (invoiceListState === INVOICE_LIST_STATE.ALL) {
                return true;
            } else if (invoiceListState === INVOICE_LIST_STATE.ALL_OPEN) {
                return !(invoice.state === INVOICE_STATE.PAID || invoice.state === INVOICE_STATE.CANCELLED);
            } else {
                let convertedInvoiceListFilterState: INVOICE_LIST_STATE | undefined = undefined;

                const invoiceState: INVOICE_STATE | undefined = invoice.state;
                if (invoiceState) {
                    convertedInvoiceListFilterState = this.convertInvoiceStateToInvoiceListState(invoiceState);
                }

                const currentInvoiceListFilterState: INVOICE_LIST_STATE = invoiceListState;

                return convertedInvoiceListFilterState === currentInvoiceListFilterState;
            }
        });

        invoices.forEach((invoice) => {
            this.setBatchProcessMapEntry(invoice.number ?? 0, newSelected);
        });
    };

    deselectAllInvoices = () => {
        this.batchProcessMap = {};
        this.hasBatchProcessMapInitialized = false;
        this.isBatchProcessing = false;
        this.currentBatchProcessIndex = 0;

        // set all values from batchProcessMap to false
        Object.keys(this.batchProcessMap).forEach((key) => {
            this.batchProcessMap[Number(key)] = false;
        });
    };
    resetAreAllInvoicesOfStateSelected = () => {
        const resetState: ListStateSelectedMap = {} as ListStateSelectedMap;

        // Loop through all enum values to ensure all possible states are reset
        Object.values(INVOICE_LIST_STATE).forEach((state) => {
            // Only process numeric enum values (skip reverse mappings)
            if (typeof state === "number") {
                resetState[state] = false;
            }
        });

        this.areAllInvoicesOfStateSelected = resetState;
    };

    setIsBatchProcessing = (isBatchProcessing: boolean) => {
        this.isBatchProcessing = isBatchProcessing;
    };

    setCurrentBatchProcessIndex = (currentBatchProcessIndex: number) => {
        this.currentBatchProcessIndex = currentBatchProcessIndex;
    };

    setNextBatchProcessIndex = () => {
        const maxIndex = this.selectedBatchProcessInvoiceNumbers.length - 1;

        let nextBatchProcessIndex = this.currentBatchProcessIndex + 1;

        if (nextBatchProcessIndex > maxIndex) {
            nextBatchProcessIndex = 0;
        }

        this.currentBatchProcessIndex = nextBatchProcessIndex;
    };

    setPreviousBatchProcessIndex = () => {
        const maxIndex = this.selectedBatchProcessInvoiceNumbers.length - 1;

        let previousBatchProcessIndex = this.currentBatchProcessIndex - 1;

        if (previousBatchProcessIndex < 0) {
            previousBatchProcessIndex = maxIndex;
        }

        this.currentBatchProcessIndex = previousBatchProcessIndex;
    };

    setBatchProcessMapInitialized = (hasBatchProcessMapInitialized: boolean) => {
        this.hasBatchProcessMapInitialized = hasBatchProcessMapInitialized;
    };

    terminateBatchProcess = () => {
        this.isBatchProcessing = false;

        // set all values from batchProcessMap to false
        Object.keys(this.batchProcessMap).forEach((key) => {
            this.batchProcessMap[Number(key)] = false;
        });

        this.currentBatchProcessIndex = 0;
        this.hasBatchProcessMapInitialized = false;
    };

    downloadInvoiceFiles = async () => {
        this.setIsDownloadingInvoiceFiles(true);

        const selectedFileIds: string[] = this.selectedFileIds;

        if (selectedFileIds.length === 1) {
            const fileIdToDownload = selectedFileIds[0];
            let fileName = undefined;
            const invoice = this.invoices.find((i) => i.invoiceFile?.fileId === fileIdToDownload);

            // invoice date in yyyyMMdd format -> 15.05.2025 = 20250515
            const invoiceDate = invoice?.date ? format(new Date(invoice.date), "yyyyMMdd") : "";
            if (invoice) {
                fileName = `${invoice.number ?? ""}-${invoice.creditor?.name ?? ""}-${invoiceDate}-${
                    invoice.invoiceNumber ?? ""
                }.pdf`;
            }

            await this.rootStore.fileStore.downloadFileAndTriggerDownload(fileIdToDownload, fileName);
        } else if (selectedFileIds.length > 1) {
            await this.downloadInvoiceFilesZip();
        }

        this.setIsDownloadingInvoiceFiles(false);
    };

    downloadInvoiceFilesZip = async (): Promise<any | undefined> => {
        let getInvoiceFilesZipBlob = undefined;

        try {
            const role = this.rootStore.authStore.user?.role;

            const requestBody = {
                files: this.selectedFileIds
            };

            const getInvoiceFilesResponse = await Axios.getAuthInstance().post(
                NetworkConfig.getInvoiceFilesUrl,
                requestBody,
                {
                    responseType: "blob",
                    headers: {
                        "x-hasura-role": getRoleKey(role)
                    }
                }
            );

            const contentDisposition = getInvoiceFilesResponse.headers["Content-Disposition"];

            let fileName = `${format(new Date(), "yyyyMMdd_HHmm")}_${i18next.t(
                "screens.realestate.documents.title"
            )}.zip`;

            if (contentDisposition) {
                // prettier-ignore
                fileName = contentDisposition?.split("filename=")[1]?.replaceAll("\"", "");
            }

            getInvoiceFilesZipBlob = getInvoiceFilesResponse.data;
            this.rootStore.fileStore.triggerDownload(getInvoiceFilesZipBlob, fileName);
        } catch (error) {
            console.error("Error downloading invoice files: ", error);
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("stores.file_store.error_downloading_file"),
                MessageType.ERROR
            );
        }

        return getInvoiceFilesZipBlob;
    };

    navigateToNextBatchProcess = () => {
        this.setNextBatchProcessIndex();

        const route = selectRoute(Route.invoiceDetails, this.rootStore.authStore.user?.role, {
            invoicenumber: this.selectedBatchProcessInvoiceNumbers[this.currentBatchProcessIndex].toString()
        });

        this.rootStore.navStore.navigateTo(route);
    };

    handleBatchProcessCheckboxClick = (invoiceNumber: number) => {
        const isCurrentlyChecked = this.batchProcessMap[invoiceNumber] ?? false;

        const newChecked = !isCurrentlyChecked; // Get the new state directly from the event

        if (newChecked) {
            this.setBatchProcessMapEntry(invoiceNumber, true);
        } else {
            this.setBatchProcessMapEntry(invoiceNumber, false);
        }
    };

    setInvoices = (invoices: Invoice[]) => {
        this.invoices = invoices;
    };

    loadInvoices = async () => {
        this.setIsLoadingInvoices(true);

        const loadAllInvoices = this.rootStore.invoiceListStore.loadAllInvoices;

        const { data }: { data: GetAllInvoices } = await apolloClientInstance.query<GetAllInvoices>({
            query: loadAllInvoices ? GET_ALL_INVOICES : GET_ALL_OPEN_INVOICES,
            fetchPolicy: "no-cache"
        });

        if (data) {
            const allInvoices: Invoice[] = data.cred_invoices.map((invoice) => {
                const newInvoiceData = this.getInvoiceObjectFromGetAllInvoicesQuery(invoice);
                return new Invoice(this.rootStore, newInvoiceData);
            });

            this.setInvoices(allInvoices);
        }

        this.rootStore.invoiceListStore.setLastReloadTime(new Date());

        this.setIsLoadingInvoices(false);
    };

    initialLoadInvoices = async () => {
        this.setIsLoadingInvoices(true);

        this.setInitialLoadInvoicesCompleted(false);

        const { data: dataFirstInvoicesTranch }: { data: GetAllInvoices } =
            await apolloClientInstance.query<GetAllInvoices>({
                query: GET_ALL_OPEN_INVOICES,
                fetchPolicy: "no-cache",
                variables: { limit: 100 }
            });

        if (dataFirstInvoicesTranch) {
            const first100Invoices: Invoice[] = dataFirstInvoicesTranch.cred_invoices.map((invoice) => {
                const newInvoiceData = this.getInvoiceObjectFromGetAllInvoicesQuery(invoice);

                return new Invoice(this.rootStore, newInvoiceData);
            });

            this.setInvoices(first100Invoices);
        }

        const { data: dataRemainingInvoices }: { data: GetAllInvoices } =
            await apolloClientInstance.query<GetAllInvoices>({
                query: GET_ALL_OPEN_INVOICES,
                fetchPolicy: "no-cache",
                variables: { limit: null, offset: 100 }
            });

        if (dataRemainingInvoices) {
            const remainingInvoices: Invoice[] = dataRemainingInvoices.cred_invoices.map((ticket) => {
                const newInvoiceData = this.getInvoiceObjectFromGetAllInvoicesQuery(ticket);
                return new Invoice(this.rootStore, newInvoiceData);
            });

            const allInvoices = this.invoices.concat(remainingInvoices);
            this.setInvoices(allInvoices);
        }

        const invoiceListStore = this.rootStore.invoiceListStore;

        if (!invoiceListStore.hasBatchProcessMapInitialized) {
            this.invoices.forEach((invoice) => {
                if (invoice.number) {
                    invoiceListStore.setBatchProcessMapEntry(invoice.number, false);
                }
            });

            invoiceListStore.setBatchProcessMapInitialized(true);
        }

        this.rootStore.invoiceListStore.setLastReloadTime(new Date());

        this.setInitialLoadInvoicesCompleted(true);
        this.setIsLoadingInvoices(false);
    };

    getInvoiceObjectFromGetAllInvoicesQuery = (invoice: GetAllInvoices_cred_invoices): IInvoiceProperties => {
        const { zip, city } =
            invoice.realestate && invoice.realestate.houses.length > 0
                ? { zip: invoice.realestate.houses[0].zip ?? "", city: invoice.realestate.houses[0].city ?? "" }
                : { zip: "", city: "" };

        const realestate: IRealestate | undefined = invoice.realestate
            ? {
                  id: invoice.realestate.id,
                  number: invoice.realestate.number,
                  name: invoice.realestate.name,
                  zip,
                  city
              }
            : undefined;

        const creditor: ICreditor | undefined = invoice.creditor
            ? {
                  id: invoice.creditor.id,
                  name: invoice.creditor.name,
                  number: invoice.creditor.number ?? undefined,
                  personId: invoice.creditor.personid ?? undefined
              }
            : undefined;

        const state = getInvoiceStateById(invoice.state);

        let assignedUser: IAssignedUser | null = this.getAssignedUserByInvoiceState(
            state,
            invoice.insertuserinfo,
            invoice.workflowusers
        );

        const invoiceFile: IInvoiceFile | undefined =
            invoice.invoicefiles.length > 0
                ? {
                      fileId: invoice.invoicefiles[0].fileid,
                      state: INVOICE_FILE_STATE.LOADED_FROM_DB
                  }
                : undefined;

        const newInvoiceData: IInvoiceProperties = {
            id: invoice.id,
            invoiceType: invoice.type as INVOICE_TYPE,
            number: invoice.number,
            state: getInvoiceStateById(invoice.state),
            invoiceNumber: invoice.invoicenumber ?? undefined,
            amount: invoice.amount ?? undefined,
            date: invoice.date,
            dueDate: invoice.duedate,
            paidDate: invoice.date,
            realestate: realestate,
            assignedUser: assignedUser ?? undefined,
            creditor: creditor,
            invoiceFile: invoiceFile
        };

        return newInvoiceData;
    };

    getAssignedUserByInvoiceState = (
        invoiceState: INVOICE_STATE,
        insertUserInfo:
            | GetAllInvoices_cred_invoices_insertuserinfo
            | GetInvoiceByNumber_cred_invoices_insertuserinfo
            | null,
        workflowUsers: GetAllInvoices_cred_invoices_workflowusers[] | GetInvoiceByNumber_cred_invoices_workflowusers[]
    ) => {
        let assignedUser: IAssignedUser | null = null;

        switch (invoiceState) {
            case INVOICE_STATE.NEW:
            case INVOICE_STATE.ASSIGN_WORKFLOW_USERS:
            case INVOICE_STATE.RESTART_AFTER_REJECTED:
                assignedUser = insertUserInfo ?? null;
                break;
            case INVOICE_STATE.READY_FOR_VISA:
                assignedUser =
                    workflowUsers.find((workflowUser) => workflowUser.role === Number(WORKFLOWUSER_ROLE.VISA_1))
                        ?.user ?? null;
                break;
            case INVOICE_STATE.VISA_1_OK:
                assignedUser =
                    workflowUsers.find((workflowUser) => workflowUser.role === Number(WORKFLOWUSER_ROLE.VISA_2))
                        ?.user ?? null;
                break;
            case INVOICE_STATE.VISA_2_OK:
                assignedUser =
                    workflowUsers.find((workflowUser) => workflowUser.role === Number(WORKFLOWUSER_ROLE.VISA_3))
                        ?.user ?? null;
                break;
            case INVOICE_STATE.FULLY_SIGHTED:
            case INVOICE_STATE.SENT_TO_ERP:
            case INVOICE_STATE.IMPORTED_IN_ERP:
                assignedUser =
                    workflowUsers.find((workflowUser) => workflowUser.role === Number(WORKFLOWUSER_ROLE.ACCOUNTANT))
                        ?.user ?? null;
                break;
            case INVOICE_STATE.REJECTED:
                assignedUser =
                    workflowUsers.find((workflowUser) => workflowUser.role === Number(WORKFLOWUSER_ROLE.REJECTED_TO))
                        ?.user ?? null;
                break;
            case INVOICE_STATE.CANCELLED:
                assignedUser =
                    workflowUsers.find((workflowUser) => workflowUser.role === Number(WORKFLOWUSER_ROLE.CANCELLED_BY))
                        ?.user ?? null;
                break;
        }

        return assignedUser;
    };

    startLoadInvoicesTimer = () => {
        this.loadInvoicesTimer = setInterval(() => {
            this.loadInvoices();
        }, INVOICE_LIST_REFRESH_TIME_IN_SECONDS * 1000);
    };

    setInitialLoadInvoicesCompleted = (initialLoadInvoicesCompleted: boolean) => {
        this.initialLoadInvoicesCompleted = initialLoadInvoicesCompleted;
    };

    clearLoadInvoicesTimer = () => {
        if (this.loadInvoicesTimer) {
            clearInterval(this.loadInvoicesTimer);
            this.loadInvoicesTimer = undefined;
        }
    };

    setLastReloadTime = (time: Date) => {
        this.lastReloadTime = time;
    };

    updateCurrentTime = () => {
        this.currentTime = new Date();
    };

    // Computed values
    get invoiceStats() {
        const allInvoices = [...this.filteredInvoices];

        const getNumberOfTicketsByState = (invoiceListState: INVOICE_LIST_STATE) => {
            switch (invoiceListState) {
                case INVOICE_LIST_STATE.NEW:
                    return allInvoices.filter(
                        (invoice) =>
                            invoice.state !== undefined &&
                            invoice.state >= INVOICE_STATE.NEW &&
                            invoice.state <= INVOICE_STATE.RESTART_AFTER_REJECTED
                    ).length;
                case INVOICE_LIST_STATE.TO_APPROVE:
                    return allInvoices.filter(
                        (invoice) =>
                            invoice.state !== undefined &&
                            invoice.state >= INVOICE_STATE.READY_FOR_VISA &&
                            invoice.state <= INVOICE_STATE.FULLY_SIGHTED
                    ).length;
                case INVOICE_LIST_STATE.PAY:
                    return allInvoices.filter(
                        (invoice) =>
                            invoice.state !== undefined &&
                            invoice.state >= INVOICE_STATE.SENT_TO_ERP &&
                            invoice.state <= INVOICE_STATE.IMPORTED_IN_ERP
                    ).length;
                case INVOICE_LIST_STATE.ALL_OPEN:
                    return allInvoices.filter(
                        (invoice) =>
                            invoice.state !== undefined &&
                            (invoice.state < INVOICE_STATE.PAID || invoice.state === INVOICE_STATE.REJECTED)
                    ).length;
                default:
                    return allInvoices.filter(
                        (invoice) => invoice.state !== undefined && Number(invoice.state) === Number(invoiceListState)
                    ).length;
            }
        };

        return {
            new: getNumberOfTicketsByState(INVOICE_LIST_STATE.NEW),
            toApprove: getNumberOfTicketsByState(INVOICE_LIST_STATE.TO_APPROVE),
            pay: getNumberOfTicketsByState(INVOICE_LIST_STATE.PAY),
            paid: getNumberOfTicketsByState(INVOICE_LIST_STATE.PAID),
            rejected: getNumberOfTicketsByState(INVOICE_LIST_STATE.REJECTED),
            cancelled: getNumberOfTicketsByState(INVOICE_LIST_STATE.CANCELLED),
            allOpen: getNumberOfTicketsByState(INVOICE_LIST_STATE.ALL_OPEN),
            total: { total: this.invoices.length, filtered: allInvoices.length }
        };
    }

    get nextBatchProcessInvoiceNumber() {
        const maxIndex = this.selectedBatchProcessInvoiceNumbers.length - 1;
        let nextBatchProcessIndex = this.currentBatchProcessIndex + 1;

        if (nextBatchProcessIndex > maxIndex) {
            nextBatchProcessIndex = 0;
        }

        const nextBatchProcessInvoiceNumber = this.selectedBatchProcessInvoiceNumbers[nextBatchProcessIndex];

        return nextBatchProcessInvoiceNumber;
    }

    get previousBatchProcessInvoiceNumber() {
        const maxIndex = this.selectedBatchProcessInvoiceNumbers.length - 1;

        let previousBatchProcessIndex = this.currentBatchProcessIndex - 1;

        if (previousBatchProcessIndex < 0) {
            previousBatchProcessIndex = maxIndex;
        }

        const previousBatchProcessInvoiceNumber = this.selectedBatchProcessInvoiceNumbers[previousBatchProcessIndex];

        return previousBatchProcessInvoiceNumber;
    }

    get filteredInvoices() {
        const filter = this.invoiceFilter;
        let filtered = this.invoices;

        // Show only invoices of the current user
        if (filter.showOnlyMyInvoices) {
            filtered = filtered.filter(
                (invoice) => invoice.assignedUser?.id === this.rootStore.authStore.user?.userid || !invoice.assignedUser
            );
        }

        // Show only invoices from the realestates the user has access to
        if (filter.showOnlyInvoicesFromMyRealestates) {
            filtered = filtered.filter((invoice) =>
                this.realestateUserData.some((user) => user.realestateid === invoice.realestate?.id)
            );
        }

        // Filter by type
        if (filter.types.length > 0) {
            filtered = filtered.filter((invoice) => invoice.invoiceType && filter.types.includes(invoice.invoiceType));
        }

        // Filter by search query
        if (filter.query !== "") {
            const query = filter.query.toLowerCase();
            filtered = filtered.filter((invoice) => invoice.searchString.includes(query));
        }

        return filtered.slice().sort((lhs, rhs) => {
            if (filter.sort === SORT.DATE_OLDEST) {
                return (lhs.number ?? 0) - (rhs.number ?? 0);
            } else if (filter.sort === SORT.DATE_NEWEST) {
                return (rhs.number ?? 0) - (lhs.number ?? 0);
            }
            return 0;
        });
    }

    get selectedBatchProcessInvoiceNumbers() {
        return Object.entries(this.batchProcessMap)
            .filter(([_, value]) => value === true)
            .map(([invoiceNumber]) => Number(invoiceNumber))
            .sort((a, b) => Number(b) - Number(a));
    }

    get hasAnySelectedBatchProcessInvoice() {
        return Object.entries(this.batchProcessMap).some(([_, value]) => value === true);
    }

    // returns the fileids
    get selectedFileIds() {
        const selectedFileIds: string[] = [];

        this.selectedBatchProcessInvoiceNumbers.forEach((invoiceNumber) => {
            const invoice = this.invoices.find((i) => i.number === invoiceNumber);
            if (invoice && invoice.invoiceFile && invoice.invoiceFile.fileId) {
                selectedFileIds.push(invoice.invoiceFile.fileId);
            }
        });
        return selectedFileIds;
    }

    get lastReloadTimeText(): string {
        if (!this.lastReloadTime) {
            return "";
        }

        const diffInMs = this.currentTime.getTime() - this.lastReloadTime.getTime();
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

        if (diffInMinutes < 1) {
            return "Gerade aktualisiert";
        } else if (diffInMinutes === 1) {
            return "Zuletzt vor 1 Minute aktualisiert";
        } else {
            return `Zuletzt vor ${diffInMinutes} Minuten aktualisiert`;
        }
    }
}
